import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { Avatar, Button, Chip, Input } from '../../components/Ui';
import ImageUploader from '../../components/ImageUploader';
import { Reveal } from '../../components/Motion';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const GENDERS = ['male', 'female', 'other'];

const EditProfilePage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const toast = useToast();

  const [form, setForm] = useState({
    name: (user && user.name) || '',
    email: (user && user.email) || '',
    avatar: (user && user.avatar) || '',
    city: (user && user.city) || '',
    gender: (user && user.gender) || '',
    skills: ((user && user.skills) || []).join(', '),
    experienceYears: String((user && user.experienceYears) || ''),
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.user.update({
        avatar: form.avatar,
        name: form.name.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        gender: form.gender,
        skills: form.skills.split(',').map((s) => s.trim()).filter(Boolean),
        experienceYears: Number(form.experienceYears) || 0,
      });
      setUser(res.data);
      toast.success('Your profile has been updated.');
      navigate(-1);
    } catch (err) {
      toast.error(err.message, 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 620 }}>
      <PageHeader title="Edit profile" subtitle="Keep your details up to date" />

      <Reveal className="card sh-sm">
        <div className="row" style={{ gap: 14, marginBottom: 18 }}>
          <Avatar name={form.name || (user && user.name)} uri={form.avatar} size={64} />
          <div>
            <div className="t-title">{form.name || 'Your name'}</div>
            <div className="t-small">+91 {user && user.phone}</div>
            <div className="t-tiny" style={{ marginTop: 3 }}>Your number cannot be changed here.</div>
          </div>
        </div>

        <form onSubmit={save}>
          <ImageUploader
            label="Profile picture"
            hint="Shown on your profile and to employers if you are looking for a job."
            folder="avatar"
            multiple={false}
            value={form.avatar}
            onChange={(v) => setForm({ ...form, avatar: v })}
          />
          <Input
            label="Full name"
            icon="👤"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="Email"
            icon="✉️"
            type="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Input
            label="City"
            icon="📍"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />

          <label className="field-label">Gender</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
            {GENDERS.map((g) => (
              <Chip
                key={g}
                label={g.charAt(0).toUpperCase() + g.slice(1)}
                active={form.gender === g}
                onClick={() => setForm({ ...form, gender: g })}
              />
            ))}
          </div>

          <Input
            label="Skills"
            placeholder="e.g. Driving, Cooking, Sales"
            hint="Separate with commas. Recruiters see these on your job applications."
            value={form.skills}
            onChange={(e) => setForm({ ...form, skills: e.target.value })}
          />
          <Input
            label="Years of experience"
            inputMode="numeric"
            value={form.experienceYears}
            onChange={(e) => setForm({ ...form, experienceYears: e.target.value.replace(/\D/g, '') })}
          />

          <Button title="Save changes" type="submit" loading={saving} size="lg" full />
        </form>
      </Reveal>
    </div>
  );
};

export default EditProfilePage;
